import React, { useEffect, useState } from 'react';
import { Gate } from './components/Gate';
import { WelcomeLetter } from './components/WelcomeLetter';
import { ProjectSearch } from './components/ProjectSearch';
import { BookGrid } from './components/BookGrid';
import { BonusStack } from './components/BonusStack';
import { UnlockStack } from './components/UnlockStack';
import { AgeLibraries } from './components/AgeLibraries';
import { ThreeSteps } from './components/ThreeSteps';
import { CommunityCard } from './components/CommunityCard';
import { accessLabel, mergePacks, unlockedFromUrl } from './data/tiers';
import type { PackId } from './data/tiers';
const STORE_KEY = 'workshop-member';
interface Member {
  email: string;
  unlocked: PackId[];
}
interface AppProps {
  startSignedIn?: boolean;
  startUnlocked?: PackId[];
}

/* localStorage rather than sessionStorage: this is a delivery page a parent
   comes back to for months, and making them retype their address every visit
   is exactly how people decide they have "lost" their access. */
function readStored(): Member | null {
  try {
    const saved = window.localStorage.getItem(STORE_KEY) ?? window.sessionStorage.getItem(STORE_KEY);
    if (!saved) return null;
    if (saved.startsWith('{')) {
      const parsed = JSON.parse(saved) as Partial<Member> & {
        tier?: string;
      };
      if (!parsed?.email) return null;
      return {
        email: parsed.email,
        unlocked: parsed.unlocked ?? []
      };
    }
    /* the very first version stored a bare email string */
    return {
      email: saved,
      unlocked: []
    };
  } catch {
    /* private windows block storage; the gate simply shows again */
    return null;
  }
}
function store(member: Member) {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(member));
  } catch {
    /* ignore */
  }
}
export function App({
  startSignedIn = false,
  startUnlocked = []
}: AppProps) {
  const [member, setMember] = useState<Member | null>(startSignedIn ? {
    email: 'member@example.com',
    unlocked: startUnlocked
  } : null);

  /* Whatever the delivery link carries, held separately so it still applies to
     somebody who has not typed their email yet. */
  const [fromLink, setFromLink] = useState<PackId[]>([]);
  useEffect(() => {
    if (startSignedIn) return;
    const linkPacks = typeof window === 'undefined' ? [] : unlockedFromUrl(window.location.search);
    setFromLink(linkPacks);
    const saved = readStored();
    if (!saved) return;
    const merged = {
      email: saved.email,
      unlocked: mergePacks(saved.unlocked, linkPacks)
    };
    setMember(merged);
    if (linkPacks.length) store(merged);
  }, [startSignedIn]);
  const signIn = (email: string) => {
    const next = {
      email,
      unlocked: fromLink
    };
    store(next);
    setMember(next);
  };
  const signOut = () => {
    try {
      window.localStorage.removeItem(STORE_KEY);
      window.sessionStorage.removeItem(STORE_KEY);
    } catch {
      /* ignore */
    }
    setMember(null);
  };
  if (member === null) {
    return <Gate onUnlock={signIn} data-id="element-0" data-mp="2stplddl52z3i" />;
  }
  return <div className="blueprint min-h-screen w-full bg-ground" data-id="element-1" data-mp="18dhq9zh8ks3w">
      <header className="border-b-2 border-ink/10 bg-surface/70" data-id="element-2" data-mp="18by986nrad12">
        <div className="mx-auto flex max-w-[76rem] flex-col gap-3 px-5 py-5 sm:flex-row sm:items-baseline sm:justify-between sm:px-8" data-id="element-3" data-mp="1d62gkr0xon2o">
          <div className="flex items-baseline gap-3" data-id="element-4" data-mp="13eod685v3349">
            <span className="font-display text-[19px] font-semibold text-ink" data-id="element-5" data-mp="13fg61b82qmqu">Little Engineer</span>
            <span aria-hidden="true" className="h-3 w-px bg-rule" data-id="element-6" data-mp="1r5k8vgdfuigs" />
            <span className="smallcaps font-sans text-[10px] text-gold" data-id="element-7" data-mp="1w3vj3s7nauzj">
              {accessLabel(member.unlocked)}
            </span>
          </div>
          <div className="flex items-baseline gap-4" data-id="element-8" data-mp="1i5ni997hn9j3">
            <span className="font-sans text-[12px] text-muted" data-id="element-9" data-mp="1m4ehpmzff39d">{member.email}</span>
            <button type="button" onClick={signOut} className="font-sans text-[12px] text-muted underline decoration-rule underline-offset-4 transition-colors duration-150 ease-out hover:text-ink hover:decoration-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink" data-id="element-10" data-mp="6nw7m4gk0tgq">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[76rem] px-5 pb-24 pt-14 sm:px-8 sm:pt-20" data-id="element-11" data-mp="h843ui792p19">
        <div className="flex flex-col gap-16 sm:gap-24" data-id="element-12" data-mp="c90yewncygsr">
          <WelcomeLetter data-id="element-13" data-mp="c6eowrgwpr7w" />
          <BookGrid data-id="element-14" data-mp="19wjd0o3pad4o" />
          <ProjectSearch data-id="element-15" data-mp="19uygwu1qmm4p" />

          <div className="flex items-center gap-5" aria-hidden="true" data-id="element-16" data-mp="19v89mpe637e3">
            <span className="h-[2px] flex-1 bg-gold/45" data-id="element-17" data-mp="14y854iwyucp3" />
            <span className="font-display text-[11px] font-semibold uppercase tracking-[0.16em] text-gold" data-id="element-18" data-mp="zxtxv336v6s4">
              That was the collection. Now the extras
            </span>
            <span className="h-[2px] flex-1 bg-gold/45" data-id="element-19" data-mp="vyl1rrkoewte" />
          </div>

          <BonusStack data-id="element-20" data-mp="3kpm86fntddwx" />
          <UnlockStack unlocked={member.unlocked} data-id="element-21" data-mp="3klq0llaifg5f" />
          <AgeLibraries data-id="element-22" data-mp="3glefy8i5dfty" />
          <ThreeSteps data-id="element-23" data-mp="3gl63ghpxscja" />
          <CommunityCard memberEmail={member.email} data-id="element-24" data-mp="3goty5k74b4zl" />
        </div>
      </main>

      <footer className="border-t border-rule bg-surface/60" data-id="element-25" data-mp="1w69nu0oud1qm">
        <div className="mx-auto flex max-w-[76rem] flex-col gap-2 px-5 py-8 font-sans text-[13px] text-muted sm:flex-row sm:items-baseline sm:justify-between sm:px-8" data-id="element-26" data-mp="2071ymydd3ep0">
          <p data-id="element-27" data-mp="29vjur2ap4xmb">Little Engineer Mega Kit · The Workshop</p>
          <p data-id="element-28" data-mp="29xejyst03bw7">Files are for your household. Please do not share the link.</p>
        </div>
      </footer>
    </div>;
}